import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import {
  ContextProviderEventType,
  ContextProviderNode,
  ContextProviderNodeChangedNotification,
  RefreshPropertyGridEvent,
} from '@decisions/server/DecisionsFramework/Design/Properties';
import { isDataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { findContextChildByPath } from 'components/property-editors/utils/ProviderNodeUtils';
import { createDataPairValue } from 'utils/DataPairUtils';
import { applyValidationUpdates } from 'components/property-editors/PropertyGrid/utils/PropertyGridUtils';

export enum ContextActionType {
  /** ContextProviderEventType.Add */
  Add = 1,
  /** ContextProviderEventType.Update */
  Update = 2,
  /** ContextProviderEventType.Delete */
  Delete = 3,
  /** Pass a new snapshot as state */
  NewSnapshot = 4,
  /** Remove current snapshot, triggering a refresh from ContextProviderService */
  ClearSnapshot = 5,
  SetNodeValue = 6,
  /** For occasional malformed data */
  Ignore = 7,
}

export interface ContextChangeAction {
  value?: any;
  path?: string[];
  type?: ContextActionType;
  validationIssues?: ValidationIssue[];
  skipValidationUpdate?: boolean;
}

/**
 * Updates context provider nodes by path within the ROOT, based on context provider node update events.
 *
 * @param state to update
 * @param changes to push into local state
 */

export function contextProviderNodeReducer(
  state: ContextProviderNode | undefined,
  change: ContextChangeAction
): ContextProviderNode | undefined {
  switch (change.type) {
    case ContextActionType.Ignore:
      return state;
    case undefined:
    case ContextActionType.NewSnapshot:
      // if it's a snapshot, use the new snapshot:
      return change.value;
    case ContextActionType.ClearSnapshot:
      return; // returning a falsy value will trigger a refresh event
  }

  if (!state) {
    // because we are sometimes loading this from an editingContextId string, the state will be initially
    // undefined in those cases. we should never get here, but makes TS happy
    return state;
  }

  // make a shallow copy to ensure change events are triggered
  state = { ...state }; // or else turn off useMemo for PropertyGrid filtering.

  // Clear and apply updated validations. Needs to happen in between other cases, because
  // validations for anything can come in alongside anything else.
  if (change.type !== ContextActionType.Delete && change.skipValidationUpdate !== true) {
    // We're going to skip validation processing on "Delete", because a delete event will tell us what node to remove
    // but won't have updated validations for the whole tree. This is consistent with Decisions.ClientEvents.ts.
    applyValidationUpdates(state, change.validationIssues);
  }

  // resolve parent / child
  const { value, path } = change;
  let { child, parent } = findContextChildByPath(path || [], state);
  const newChildren = [...parent.Children];

  let index = !child ? -1 : parent.Children!.indexOf(child);

  switch (change.type) {
    case ContextActionType.Delete:
      if (index !== -1) {
        newChildren.splice(index, 1);
        parent.Children = newChildren;
        return state;
      }
      break;
    case ContextActionType.Add:
      if (child) {
        // it's already in there, treat it like an update
        newChildren[index] = {
          ...newChildren[index],
          ...value,
        };
        parent.Children = newChildren;
      } else {
        parent.Children = [...newChildren, value];
      }
      return state;
    case ContextActionType.Update:
      if (index === -1) {
        // how can we update what was not there?
        console.warn('No matching elements found for path', (path || []).join(','));
        return state;
      }
      newChildren[index] = {
        ...newChildren[index],
        ...value,
      };
      parent.Children = newChildren;
      return state;
    default:
      // treating .SetNodeValue as the default, though it may not be used in production, currently
      // new objects instead of mutating them.
      if (!child) {
        return state;
      }
      child = { ...child };
      child.Value = isDataPair(value) ? value : createDataPairValue(value);
      parent.Children = [...parent.Children!];
      parent.Children[index] = child;

      break;
  }
  return state;
}

// example is input mappings, if I need to inspect the actual JSON payload...
function getRefreshAction(): ContextChangeAction {
  return {
    type: ContextActionType.ClearSnapshot,
  };
}

function isNodeChangedNotification(
  event: ContextProviderNodeChangedNotification | RefreshPropertyGridEvent
): event is ContextProviderNodeChangedNotification {
  return (event as ContextProviderNodeChangedNotification).Node !== undefined;
}

export function getNodeUpdateAction(
  event: ContextProviderNodeChangedNotification | RefreshPropertyGridEvent,
  skipValidationUpdate = false
): ContextChangeAction | undefined {
  if (isNodeChangedNotification(event)) {
    if (event.NotificationType === ContextProviderEventType.Add && !event.ProviderNode) {
      //if we get an "add provider node" event with no provider node...
      console.log('Invalid client "Add" notification received.');
      return;
    }
    return {
      path: event.Path,
      type: event.NotificationType as number,
      value: getNodeValue(event),
      validationIssues: event.ValidationIssues,
      skipValidationUpdate,
    };
  }
  return getRefreshAction();
}

function getNodeValue(event: ContextProviderNodeChangedNotification) {
  return (
    event.ProviderNode || {
      Node: event.Node,
      PropertyValue: event.PropertyValue,
    }
  );
}
